
import { useState, useMemo } from 'react';
import { OwnAccountCandidate, Transaction } from "@/types/payments";
import { toast } from "sonner";
import { useTranslations } from 'use-intl';
import { ServiceRegistry } from "@/services/ServiceRegistry";
import { ManualMatchService } from "@/services/manualMatchService";
import { PaymentService } from "@/services/paymentService";
import { settledRefusalMessage, matchRefusalTitle } from "@/utils/errorUtils";

export function useTransactionMatching(transactions: Transaction[], setTransactions: React.Dispatch<React.SetStateAction<Transaction[]>>) {
  const t = useTranslations('consultantToasts');

  /** A permanent refusal is named, never "please try again"; any other failure was already toasted by the client. */
  const toastSettledRefusal = (error: unknown) => {
    const settled = settledRefusalMessage(error);
    if (settled) {
      toast.error(matchRefusalTitle(error) ?? t('matching.alreadyMatched'), { description: settled });
    }
  };
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
  const manualMatchService = new ManualMatchService();
  const paymentService = new PaymentService();

  // Get the selected transaction for the match modal
  const selectedTransaction = useMemo(() => {
    if (!selectedTransactionId) return null;
    return transactions.find(t => t.id === selectedTransactionId) || null;
  }, [selectedTransactionId, transactions]);

  // Handle manual matching
  const handleManualMatch = (transactionId: string) => {
    setSelectedTransactionId(transactionId);
  };

  // Handle match selection. Issue #850 — returns Promise<boolean> (mirrors
  // handleConfirmPayableMatch below) so MatchSuggestionsModal can await the real write outcome
  // and only close the modal on confirmed success, instead of closing unconditionally right
  // after firing this off.
  const handleMatchSelection = async (transactionId: string, receivableId: string): Promise<boolean> => {
    try {
      // Store the manual match in Supabase
      const manualMatch = await manualMatchService.createManualMatch({
        transactionId,
        receivableId
      });

      if (manualMatch) {
        // Update the local transaction state
        setTransactions(prevTransactions =>
          prevTransactions.map(transaction =>
            transaction.id === transactionId
              ? { ...transaction, matched: true, receivableId }
              : transaction
          )
        );

        toast(t('matching.matchedReceivable', { transactionId, receivableId }));
        return true;
      } else {
        toast.error(t('matching.saveFailed'));
        return false;
      }
    } catch (error) {
      // ManualMatchApiClient already toasted any other failure with the server's own sentence
      // (e.g. FIN-1052's amount refusal); only the named FIN-968/FIN-1023 D7/AC-13 refusal is left
      // to show here, with its own title rather than the generic "Transaction already matched".
      toastSettledRefusal(error);
      return false;
    }
  };

  // Handle payable match confirmation (issue #497) — the payable-side counterpart of
  // handleMatchSelection above, reusing #462's existing PaymentService.confirmPayableMatch
  // write path. Async and returns a boolean (rather than being sync/void like
  // handleMatchSelection) so MatchSuggestionsModal only closes the modal on confirmed success —
  // mirrors PayableMatchSuggestionsModal's onConfirmMatch contract and useInvoiceActions'
  // handleConfirmMatch.
  const handleConfirmPayableMatch = async (payableId: string, transactionId: string): Promise<boolean> => {
    try {
      await paymentService.confirmPayableMatch(payableId, transactionId);

      // Update the local transaction state
      setTransactions(prevTransactions =>
        prevTransactions.map(transaction =>
          transaction.id === transactionId
            ? { ...transaction, matched: true, payableId }
            : transaction
        )
      );

      toast(t('matching.matchedPayable', { transactionId, payableId }));
      return true;
    } catch (error) {
      // PaymentApiClient already reported and toasted any other failure with the server's own
      // sentence; a second generic toast here only duplicated it (FIN-1052). FIN-968 (widened by
      // FIN-1023 D7/AC-13): a permanent refusal is named, never "please try again".
      toastSettledRefusal(error);
      return false;
    }
  };

  // FIN-976 F6: pair a bank transaction with a tax account row by hand. The verification is posted
  // in the background, so the row shows the matched mark now and its Review link once it exists.
  const handleConfirmOwnAccountMatch = async (
    transaction: Transaction,
    candidate: Pick<OwnAccountCandidate, "legKind" | "legId">,
  ): Promise<boolean> => {
    if (!transaction.bankTransactionId) return false;
    try {
      await ServiceRegistry.getTransactionService().confirmOwnAccountTransfer(transaction.bankTransactionId, candidate);
      setTransactions(prevTransactions =>
        prevTransactions.map(t =>
          t.id === transaction.id ? { ...t, matched: true, ownAccountTransfer: { suggestionId: null } } : t
        )
      );
      return true;
    } catch (error) {
      // TransactionApiClient already toasted any other failure; a named refusal gets its own title.
      toastSettledRefusal(error);
      return false;
    }
  };

  return {
    selectedTransactionId,
    setSelectedTransactionId,
    selectedTransaction,
    handleManualMatch,
    handleMatchSelection,
    handleConfirmPayableMatch,
    handleConfirmOwnAccountMatch
  };
}
